import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ChevronDown, ChevronUp, Info, Search, User } from 'lucide-react';
import { Search as SearchType, TaskStatus } from '../../../types/search.types';
import type { EmailMetrics } from '../../../api/results';
import type { SearchPipelineStep } from './searchPipelineConfig';

const SEARCH_CREATED_MESSAGE = 'Search created successfully! Big cities like New York may take up to 2.5 hours to collect all the data.';

const getBusinessNoun = (count: number) => count === 1 ? 'business' : 'businesses';

export const getNoWebsiteResultLabel = (count: number) => (
  `${getBusinessNoun(count)} with no website listed`
);

export const getNoWebsiteResultsCta = (count: number) => (
  `View ${count} ${getBusinessNoun(count)}`
);

export const getCreationNotificationMessage = (search: SearchType): string => {
  if (search.status !== TaskStatus.COMPLETED) return SEARCH_CREATED_MESSAGE;
  const businessCount = search.places_saved ?? search.completed_links;
  if (businessCount === 0) return 'Search completed — no matching businesses found.';

  const businessLabel = search.google_maps_requirements?.has_website === false
    ? getNoWebsiteResultLabel(businessCount)
    : getBusinessNoun(businessCount);
  return `Search completed — ${businessCount} ${businessLabel} found.`;
};

interface CompletedNoWebsiteSummaryProps {
  search: SearchType;
  dateLabel: string;
  emailMetrics: EmailMetrics | null;
  pipelineSteps: SearchPipelineStep[];
  pipelineExpanded: boolean;
  onTogglePipeline: () => void;
}

const CompletedNoWebsiteSummary: React.FC<CompletedNoWebsiteSummaryProps> = ({
  search,
  dateLabel,
  emailMetrics,
  pipelineSteps,
  pipelineExpanded,
  onTogglePipeline,
}) => {
  const businessCount = search.places_saved ?? search.completed_links;
  const decisionMakerCount = emailMetrics?.decision_makers_count ?? 0;
  const hasBusinesses = businessCount > 0;

  return (
    <>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs font-medium uppercase text-gray-500">
            <span>Search result</span>
            <span aria-hidden className="h-1 w-1 rounded-full bg-gray-300" />
            <span className="normal-case">{dateLabel}</span>
          </div>
          <h2 className="mt-1 break-words text-lg font-semibold text-gray-900">{search.search_query}</h2>
          <p className="mt-0.5 text-sm text-gray-500">{search.city?.display_name ?? '—'}</p>
        </div>
        <div className="inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-800 sm:self-auto">
          <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-green-500" />
          Completed
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-primary/20 border-l-4 border-l-primary bg-primary/5 p-5 sm:p-6">
        <div className="grid grid-cols-[44px_minmax(0,1fr)] items-center gap-x-3 gap-y-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            {hasBusinesses ? 'Prospect list ready' : 'Search completed'}
          </p>
          {hasBusinesses ? (
            <div className="col-span-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 sm:col-span-1 sm:col-start-2">
              <span className="text-4xl font-bold leading-none tabular-nums text-primary sm:text-5xl">
                {businessCount}
              </span>
              <span className="text-lg font-semibold leading-snug text-gray-900 sm:text-xl">
                {getNoWebsiteResultLabel(businessCount)}
              </span>
            </div>
          ) : (
            <p className="col-span-2 text-xl font-semibold text-gray-900 sm:col-span-1 sm:col-start-2">
              No matching businesses found
            </p>
          )}
        </div>
      </div>

      {hasBusinesses && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-200/70 text-gray-600 sm:flex">
            <Info className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="text-sm leading-relaxed text-gray-600">
              WebLeads usually finds decision makers on company websites. Because these businesses don&apos;t have one listed, most searches will return few or none. Any decision makers found through other sources will still appear in your results.
            </p>
            {decisionMakerCount > 0 && (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700">
                <User className="h-3.5 w-3.5 text-primary" />
                {decisionMakerCount} decision {decisionMakerCount === 1 ? 'maker' : 'makers'} found from other sources
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        {hasBusinesses ? (
          <Link
            className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 font-medium text-white shadow-sm transition-colors hover:bg-primary/90 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 sm:w-auto"
            to={`/searches/${search.id}/results`}
          >
            <Search className="h-4 w-4" />
            {getNoWebsiteResultsCta(businessCount)}
          </Link>
        ) : (
          <Link
            className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 font-medium text-white shadow-sm transition-colors hover:bg-primary/90 hover:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 sm:w-auto"
            to="/searches/new"
          >
            <Search className="h-4 w-4" />
            Start a new search
          </Link>
        )}
        <button
          type="button"
          aria-expanded={pipelineExpanded}
          aria-controls="completed-no-website-pipeline"
          onClick={onTogglePipeline}
          className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-md px-2 text-sm text-gray-600 transition-colors hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2 sm:ml-auto sm:w-auto"
        >
          {pipelineExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          {pipelineExpanded ? 'Hide pipeline' : 'Pipeline details'}
        </button>
      </div>

      <div
        id="completed-no-website-pipeline"
        hidden={!pipelineExpanded}
        className="mt-6 border-t border-gray-200 pt-6"
      >
        <div className="flex w-full min-w-0 items-center gap-0 overflow-x-auto pb-2">
          {pipelineSteps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex min-w-[68px] shrink-0 flex-col items-center">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white">
                  <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="mt-1 max-w-[72px] text-center text-[10px] leading-tight text-gray-500">
                  {step.compactLabel}
                </span>
              </div>
              {index < pipelineSteps.length - 1 && (
                <div className="mb-5 h-0.5 min-w-[12px] flex-1 shrink bg-primary" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </>
  );
};

export default CompletedNoWebsiteSummary;
